import { Comment } from '@common/schemas/comment.schema';
import { Comment as CommentIcon } from '@mui/icons-material';
import { Badge, Box, IconButton, Stack } from '@mui/material';
import { Editor } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { CommentPopover } from './CommentPopover';

interface CommentSidebarProps {
  editor: Editor;
  comments: Comment[];
  onResolve: (id: string) => void;
  onDelete: (id: string) => void;
}

interface CommentGroup {
  top: number;
  comments: Comment[];
}

/**
 * Sidebar overlay for displaying comment icons and popovers.
 * Implements clustering for nearby comments.
 */
export function CommentSidebar({ editor, comments, onResolve, onDelete }: CommentSidebarProps) {
  const [groups, setGroups] = useState<CommentGroup[]>([]);
  const [activeGroupIdx, setActiveGroupIdx] = useState<number | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePositions = () => {
      if (!editor || !editor.view.dom || !sidebarRef.current) return;

      const newGroups: CommentGroup[] = [];
      const editorBounds = editor.view.dom.getBoundingClientRect();

      comments.forEach(comment => {
        let markPos: number | null = null;

        // Find the first position of the comment mark or paragraph attribute
        editor.state.doc.descendants((node, pos) => {
          if (markPos !== null) return false;
          
          // Check inline marks
          node.marks.forEach(mark => {
            if (mark.type.name === 'comment' && mark.attrs.commentId === comment.id) {
              markPos = pos;
            }
          });

          // Check paragraph block attributes
          if (node.type.name === 'paragraph' && node.attrs && node.attrs.commentId === comment.id) {
            markPos = pos;
          }
          
          return markPos === null;
        });

        if (markPos !== null) {
          try {
            const coords = editor.view.coordsAtPos(markPos);
            const top = coords.top - editorBounds.top;

            // Clustering logic: group icons within 28px of each other
            const existingGroup = newGroups.find(g => Math.abs(g.top - top) < 28);
            if (existingGroup) {
              existingGroup.comments.push(comment);
            } else {
              newGroups.push({ top, comments: [comment] });
            }
          } catch (e) {
            // Silently fail if coordinates cannot be determined (e.g. pos out of sync)
          }
        }
      });

      setGroups(newGroups.sort((a, b) => a.top - b.top));
    };

    // Events that should trigger a position update
    editor.on('update', updatePositions);
    editor.on('selectionUpdate', updatePositions);

    // Track scroll in all ancestors to keep icons aligned
    const scrollContainers: HTMLElement[] = [];
    let parent = editor.view.dom.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      if (/(auto|scroll)/.test(style.overflow + style.overflowY + style.overflowX)) {
        scrollContainers.push(parent);
      }
      parent = parent.parentElement;
    }

    scrollContainers.forEach(container => container.addEventListener('scroll', updatePositions));
    window.addEventListener('resize', updatePositions);

    // Initial delay to ensure Tiptap is fully rendered
    const timer = setTimeout(updatePositions, 150);

    return () => {
      editor.off('update', updatePositions);
      editor.off('selectionUpdate', updatePositions);
      scrollContainers.forEach(container => container.removeEventListener('scroll', updatePositions));
      window.removeEventListener('resize', updatePositions);
      clearTimeout(timer);
    };
  }, [editor, comments]);

  // Close popover if user clicks outside
  useEffect(() => {
    if (activeGroupIdx === null) return;

    const handleGlobalClick = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        setActiveGroupIdx(null);
      }
    };

    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, [activeGroupIdx]);

  return (
    <Box
      ref={sidebarRef}
      sx={{
        position: 'absolute',
        right: 0,
        top: 0,
        height: '100%',
        zIndex: 50,
        pointerEvents: 'none',
      }}
    >
      {groups.map((group, idx) => (
        <Box
          key={idx}
          sx={{
            position: 'absolute',
            top: group.top,
            right: -60, // Positioned in the right gutter of the 720px content
            transition: 'top 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'auto',
            zIndex: activeGroupIdx === idx ? 100 : 1
          }}
        >
          <Badge
            badgeContent={group.comments.length > 1 ? group.comments.length : 0}
            color="primary"
            sx={{
              '& .MuiBadge-badge': {
                fontWeight: 900,
                fontSize: '0.6rem',
                minWidth: 14,
                height: 14,
                p: 0,
                border: '1px solid',
                borderColor: 'background.paper'
              }
            }}
          >
            <IconButton
              size="medium"
              onClick={(e) => {
                e.stopPropagation();
                setActiveGroupIdx(activeGroupIdx === idx ? null : idx);
              }}
              sx={{
                bgcolor: activeGroupIdx === idx ? 'primary.main' : 'background.paper',
                boxShadow: 4,
                p: 1.2,
                border: '1.5px solid',
                borderColor: activeGroupIdx === idx ? 'primary.main' : 'divider',
                color: activeGroupIdx === idx ? 'primary.contrastText' : 'primary.main',
                '&:hover': {
                  bgcolor: activeGroupIdx === idx ? 'primary.dark' : 'action.hover',
                  transform: 'scale(1.15)',
                  boxShadow: 8,
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <CommentIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Badge>

          {activeGroupIdx === idx && (
            <Box
              sx={{
                position: 'absolute',
                right: 48,
                top: -16,
                zIndex: 1000,
                pointerEvents: 'auto'
              }}
            >
              <Stack spacing={1.5} sx={{ alignItems: 'flex-end' }}>
                {group.comments.map(c => (
                  <CommentPopover
                    key={c.id}
                    comment={c}
                    onResolve={onResolve}
                    onDelete={onDelete}
                    onClose={() => setActiveGroupIdx(null)}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
